import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader, PageHeaderTab } from "../../components/PageHeader";
import { ListToolbar } from "../../components/ListToolbar";
import { Modal } from "../../components/Modal";
import { formatCurrency, formatDateShort } from "../Obras/obraFormat";
import { AgingBadge, KindBadge, RiskBadge, StatusBadge } from "../../contasReceber/badges";
import {
  ApiError,
  changeStatus,
  createClientInvoice,
  createCreditNote,
  deleteClientInvoice,
  getClientInvoices,
  getClientInvoiceStatuses,
  getCollectionActivities,
  getCollectionDashboard,
  getReceivablesAging,
  logCollectionActivity,
  previewCollectionEmail,
  registerPayment,
  sendCollectionEmail,
  updateClientInvoice,
  type ClientInvoice,
  type ClientInvoiceFilters,
  type ClientInvoiceKind,
  type ClientInvoiceStatus,
  type CollectionActivity,
  type CollectionDashboard,
  type CollectionDashboardRow,
  type ReceivablesAging,
} from "../../contasReceber/contasReceberApi";
import { getWorks, type Work } from "../../obras/obrasApi";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";
import { useLookupOptions } from "../../lookups/useLookupOptions";

type TabId = "aging" | "invoices" | "cobranca";

// Códigos de fase do workflow de cobrança → rótulo PT (espelha CollectionCodes no backend).
const STAGE_LABELS: Record<string, string> = {
  pre_due: "Pré-vencimento",
  due_notice: "Aviso de vencimento",
  soft: "Cobrança suave",
  firm: "Cobrança firme",
  other: "Outro",
};

// Canais para registo manual de uma ação de cobrança.
const CHANNEL_OPTIONS: { code: string; label: string }[] = [
  { code: "phone", label: "Telefone" },
  { code: "note", label: "Nota" },
  { code: "letter", label: "Carta" },
  { code: "email", label: "Email" },
];

// Fase de fallback quando a fatura ainda não tem fase sugerida (sem vencimento, etc.).
const fallbackStage = (row: CollectionDashboardRow): string =>
  row.suggestedStage ?? "due_notice";

const parseNumber = (value: string): number | null => {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const todayInput = (): string => new Date().toISOString().slice(0, 10);

const inputClass =
  "px-3 py-2 border border-border rounded bg-background text-foreground text-sm";

interface InvoiceForm {
  clientId: string;
  workId: string;
  description: string;
  issueDate: string;
  dueDate: string;
  baseAmount: string;
  vatAmount: string;
  totalAmount: string;
  notes: string;
}

const emptyInvoiceForm = (): InvoiceForm => ({
  clientId: "",
  workId: "",
  description: "",
  issueDate: "",
  dueDate: "",
  baseAmount: "",
  vatAmount: "",
  totalAmount: "",
  notes: "",
});

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

export function ContasReceberView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Deep-link: ?faturaNumero=<n> abre a aba Faturas já filtrada por essa fatura.
  const [activeTab, setActiveTab] = useState<TabId>(() =>
    searchParams.get("faturaNumero") ? "invoices" : "aging",
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Contas a Receber"
        subtitle="Faturação a clientes, antiguidade de saldos e conta corrente"
        tabs={
          <>
            <PageHeaderTab
              label="Antiguidade (Aging)"
              isActive={activeTab === "aging"}
              onClick={() => setActiveTab("aging")}
            />
            <PageHeaderTab
              label="Faturas"
              isActive={activeTab === "invoices"}
              onClick={() => setActiveTab("invoices")}
            />
            <PageHeaderTab
              label="Cobrança"
              isActive={activeTab === "cobranca"}
              onClick={() => setActiveTab("cobranca")}
            />
          </>
        }
      />
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {activeTab === "aging" ? (
          <AgingTab
            onOpenClient={(clientId) =>
              navigate("/clientes", { state: { clientId } })
            }
          />
        ) : activeTab === "invoices" ? (
          <InvoicesTab />
        ) : (
          <CobrancaTab
            onOpenClient={(clientId) =>
              navigate("/clientes", { state: { clientId } })
            }
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aging / DSO por cliente [3.6.1]
// ---------------------------------------------------------------------------

function AgingTab({
  onOpenClient,
}: {
  onOpenClient: (clientId: number) => void;
}) {
  const [aging, setAging] = useState<ReceivablesAging | null>(null);
  const [asOf, setAsOf] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (asOfValue: string) => {
    setIsLoading(true);
    setError(null);
    try {
      setAging(await getReceivablesAging(asOfValue || undefined));
    } catch (err) {
      setError(errorMessage(err, "Não foi possível carregar a antiguidade de saldos."));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(asOf);
  }, [load, asOf]);

  const rows = aging?.clients ?? [];

  const money = (value: number) =>
    value === 0 ? (
      <span className="text-secondary-text">—</span>
    ) : (
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatCurrency(value)}</span>
    );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-secondary-text">
          À data de
          <input
            type="date"
            value={asOf}
            onChange={(event) => setAsOf(event.target.value)}
            className={inputClass}
          />
          {asOf !== "" && (
            <button
              type="button"
              onClick={() => setAsOf("")}
              className="text-secondary-text hover:text-foreground underline cursor-pointer"
            >
              Hoje
            </button>
          )}
        </label>
        {aging && (
          <span className="text-sm text-secondary-text">
            Total em dívida{" "}
            <span
              className="text-foreground"
              style={{ fontWeight: "var(--font-weight-semi-bold)" }}
            >
              {formatCurrency(aging.totalOutstanding)}
            </span>
          </span>
        )}
      </div>

      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: 880 }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3 text-right" style={{ width: 120 }}>Corrente</th>
              <th className="px-4 py-3 text-right" style={{ width: 110 }}>0-30</th>
              <th className="px-4 py-3 text-right" style={{ width: 110 }}>31-60</th>
              <th className="px-4 py-3 text-right" style={{ width: 110 }}>61-90</th>
              <th className="px-4 py-3 text-right" style={{ width: 110 }}>+90</th>
              <th className="px-4 py-3 text-right" style={{ width: 130 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-secondary-text">
                  A carregar…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-secondary-text">
                  Sem saldos em aberto.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.clientId}
                  className="border-b border-border last:border-b-0 cursor-pointer hover:bg-accent/30"
                  onClick={() => onOpenClient(row.clientId)}
                  title="Ver conta corrente do cliente"
                >
                  <td className="px-4 py-3 break-words">
                    <span
                      className="text-primary"
                      style={{ fontWeight: "var(--font-weight-medium)" }}
                    >
                      {row.clientName}
                    </span>
                    <span className="block text-xs text-secondary-text mt-0.5">
                      {row.invoiceCount} fatura(s) em aberto
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{money(row.current)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{money(row.d0_30)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{money(row.d31_60)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-red-600">{money(row.d61_90)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-red-700">{money(row.d90Plus)}</td>
                  <td
                    className="px-4 py-3 text-right whitespace-nowrap"
                    style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                  >
                    {formatCurrency(row.totalOutstanding)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {aging && rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-muted" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                <td className="px-4 py-3">Total</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(aging.currentTotal)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(aging.bucket0_30)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(aging.bucket31_60)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(aging.bucket61_90)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(aging.bucket90Plus)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(aging.totalOutstanding)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-xs text-secondary-text">
        Clique numa linha para abrir a conta corrente do cliente.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Faturas
// ---------------------------------------------------------------------------

function InvoicesTab() {
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState<ClientInvoice[]>([]);
  const [statuses, setStatuses] = useState<ClientInvoiceStatus[]>([]);
  const [works, setWorks] = useState<Work[]>([]);
  const { options: clientLookupOptions, loading: clientsLoading } =
    useLookupOptions("clients");

  // Pré-preenche o filtro a partir de ?faturaNumero=<n> (deep-link da conciliação).
  const [filter, setFilter] = useState(() => searchParams.get("faturaNumero") ?? "");
  const [statusFilter, setStatusFilter] = useState("");
  const [kindFilter, setKindFilter] = useState<"" | ClientInvoiceKind>("");
  const [openOnly, setOpenOnly] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals.
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState<ClientInvoice | null>(null);
  const [form, setForm] = useState<InvoiceForm>(emptyInvoiceForm());
  const [isSaving, setIsSaving] = useState(false);

  const [paymentInvoice, setPaymentInvoice] = useState<ClientInvoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");

  const [creditNoteInvoice, setCreditNoteInvoice] = useState<ClientInvoice | null>(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditBase, setCreditBase] = useState("");
  const [creditVat, setCreditVat] = useState("");
  const [creditDescription, setCreditDescription] = useState("");

  const [busyId, setBusyId] = useState<number | null>(null);

  // [3.6.4] Bloqueio de crédito ao emitir (HTTP 409): modal de autorização com motivo.
  const [blockModal, setBlockModal] = useState<{
    invoice: ClientInvoice;
    message: string;
  } | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [blockError, setBlockError] = useState<string | null>(null);
  const [isOverriding, setIsOverriding] = useState(false);

  const filters: ClientInvoiceFilters = useMemo(
    () => ({
      status: statusFilter || null,
      kind: kindFilter || null,
      open: openOnly ? true : null,
    }),
    [statusFilter, kindFilter, openOnly],
  );

  const loadInvoices = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        setInvoices(await getClientInvoices(filters));
      } catch (err) {
        setError(errorMessage(err, "Não foi possível carregar as faturas."));
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Reference data (once).
  useEffect(() => {
    getClientInvoiceStatuses()
      .then(setStatuses)
      .catch(() => setStatuses([]));
    getWorks()
      .then(setWorks)
      .catch(() => setWorks([]));
  }, []);

  const normalizedFilter = filter.trim().toLowerCase();
  const shown =
    normalizedFilter === ""
      ? invoices
      : invoices.filter((inv) =>
          [inv.number, inv.clientName, inv.workName ?? "", inv.description ?? ""]
            .join(" ")
            .toLowerCase()
            .includes(normalizedFilter),
        );

  // Only issued invoices are real debt — a rascunho is pipeline, not yet billed.
  const totalOpen = shown
    .filter(
      (inv) =>
        inv.kind === "invoice" &&
        inv.statusCode !== "rascunho" &&
        inv.statusCode !== "anulada",
    )
    .reduce((sum, inv) => sum + (inv.openAmount ?? 0), 0);

  // --- Create / edit -------------------------------------------------------
  const openCreate = () => {
    setEditInvoice(null);
    setForm(emptyInvoiceForm());
    setError(null);
    setIsCreateOpen(true);
  };

  const openEdit = (inv: ClientInvoice) => {
    setEditInvoice(inv);
    setForm({
      clientId: String(inv.clientId),
      workId: inv.workId != null ? String(inv.workId) : "",
      description: inv.description ?? "",
      issueDate: inv.issueDate ? inv.issueDate.slice(0, 10) : "",
      dueDate: inv.dueDate ? inv.dueDate.slice(0, 10) : "",
      baseAmount: inv.baseAmount != null ? String(inv.baseAmount) : "",
      vatAmount: inv.vatAmount != null ? String(inv.vatAmount) : "",
      totalAmount: inv.totalAmount != null ? String(inv.totalAmount) : "",
      notes: inv.notes ?? "",
    });
    setError(null);
    setIsCreateOpen(true);
  };

  const closeForm = () => {
    setIsCreateOpen(false);
    setEditInvoice(null);
  };

  const handleSaveInvoice = async () => {
    if (isSaving) return;
    const clientId = Number.parseInt(form.clientId, 10);
    if (!Number.isFinite(clientId) || clientId <= 0) {
      setError("Selecione um cliente.");
      return;
    }
    const base = parseNumber(form.baseAmount);
    const vat = parseNumber(form.vatAmount);
    let total = parseNumber(form.totalAmount);
    if (total === null && (base !== null || vat !== null)) {
      total = (base ?? 0) + (vat ?? 0);
    }
    const payload = {
      clientId,
      workId: form.workId ? Number.parseInt(form.workId, 10) : null,
      description: form.description.trim() || null,
      issueDate: form.issueDate || null,
      dueDate: form.dueDate || null,
      baseAmount: base,
      vatAmount: vat,
      totalAmount: total,
      notes: form.notes.trim() || null,
      currencyCode: "EUR",
    };
    setIsSaving(true);
    setError(null);
    try {
      if (editInvoice) {
        await updateClientInvoice(editInvoice.id, payload);
      } else {
        await createClientInvoice(payload);
      }
      closeForm();
      await loadInvoices(true);
    } catch (err) {
      setError(errorMessage(err, "Não foi possível guardar a fatura."));
    } finally {
      setIsSaving(false);
    }
  };

  // --- Status transitions --------------------------------------------------
  const transition = async (inv: ClientInvoice, statusCode: string) => {
    if (busyId !== null) return;
    setBusyId(inv.id);
    setError(null);
    try {
      await changeStatus(inv.id, { statusCode });
      await loadInvoices(true);
    } catch (err) {
      setError(errorMessage(err, "Não foi possível alterar o estado da fatura."));
    } finally {
      setBusyId(null);
    }
  };

  // Emitir uma fatura: pode falhar com 409 (bloqueio de crédito) → abre o modal de autorização.
  const handleEmit = async (inv: ClientInvoice) => {
    if (busyId !== null) return;
    setBusyId(inv.id);
    setError(null);
    try {
      await changeStatus(inv.id, { statusCode: "emitida" });
      await loadInvoices(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setOverrideReason("");
        setBlockError(null);
        setBlockModal({ invoice: inv, message: err.message });
      } else {
        setError(errorMessage(err, "Não foi possível alterar o estado da fatura."));
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleAuthorizeEmit = async () => {
    if (!blockModal || isOverriding) return;
    const reason = overrideReason.trim();
    if (reason === "") {
      setBlockError("Indique o motivo da autorização.");
      return;
    }
    setIsOverriding(true);
    setBlockError(null);
    try {
      await changeStatus(blockModal.invoice.id, {
        statusCode: "emitida",
        overrideBlock: true,
        overrideReason: reason,
      });
      setBlockModal(null);
      setOverrideReason("");
      await loadInvoices(true);
    } catch (err) {
      setBlockError(errorMessage(err, "Não foi possível autorizar a emissão."));
    } finally {
      setIsOverriding(false);
    }
  };

  const handleAnnul = (inv: ClientInvoice) => {
    if (!window.confirm(`Anular a fatura ${inv.number}?`)) return;
    transition(inv, "anulada");
  };

  const handleDelete = async (inv: ClientInvoice) => {
    if (busyId !== null) return;
    if (!window.confirm(`Eliminar o rascunho ${inv.number}?`)) return;
    setBusyId(inv.id);
    setError(null);
    try {
      await deleteClientInvoice(inv.id);
      setInvoices((prev) => prev.filter((item) => item.id !== inv.id));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setInvoices((prev) => prev.filter((item) => item.id !== inv.id));
      } else {
        setError(errorMessage(err, "Não foi possível eliminar o rascunho."));
      }
    } finally {
      setBusyId(null);
    }
  };

  // --- Payment -------------------------------------------------------------
  const openPayment = (inv: ClientInvoice) => {
    setPaymentInvoice(inv);
    setPaymentAmount(inv.openAmount > 0 ? String(inv.openAmount) : "");
    setPaymentDate(todayInput());
    setError(null);
  };

  const handleRegisterPayment = async () => {
    if (!paymentInvoice || isSaving) return;
    const amount = parseNumber(paymentAmount);
    if (amount === null || amount <= 0) {
      setError("Indique um valor de recebimento válido.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await registerPayment(paymentInvoice.id, {
        amount,
        paymentDate: paymentDate || null,
      });
      setPaymentInvoice(null);
      await loadInvoices(true);
    } catch (err) {
      setError(errorMessage(err, "Não foi possível registar o recebimento."));
    } finally {
      setIsSaving(false);
    }
  };

  // --- Credit note ---------------------------------------------------------
  const openCreditNote = (inv: ClientInvoice) => {
    setCreditNoteInvoice(inv);
    setCreditAmount(inv.openAmount > 0 ? String(inv.openAmount) : "");
    setCreditBase("");
    setCreditVat("");
    setCreditDescription("");
    setError(null);
  };

  const handleCreateCreditNote = async () => {
    if (!creditNoteInvoice || isSaving) return;
    const amount = parseNumber(creditAmount);
    if (amount === null || amount <= 0) {
      setError("Indique o valor da nota de crédito.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await createCreditNote(creditNoteInvoice.id, {
        amount,
        baseAmount: parseNumber(creditBase),
        vatAmount: parseNumber(creditVat),
        description: creditDescription.trim() || null,
        issueDate: null,
      });
      setCreditNoteInvoice(null);
      await loadInvoices(true);
    } catch (err) {
      setError(errorMessage(err, "Não foi possível criar a nota de crédito."));
    } finally {
      setIsSaving(false);
    }
  };

  // --- Row actions visibility ---------------------------------------------
  const rowActions = (inv: ClientInvoice) => {
    if (inv.kind === "credit_note") {
      return inv.statusCode !== "anulada" ? (
        <button
          type="button"
          className="text-destructive underline disabled:opacity-50"
          disabled={busyId === inv.id}
          onClick={() => handleAnnul(inv)}
        >
          Anular
        </button>
      ) : (
        <span className="text-secondary-text">—</span>
      );
    }
    const isDraft = inv.statusCode === "rascunho";
    const isAnnulled = inv.statusCode === "anulada";
    const isPaid = inv.statusCode === "paga";
    const canPay = !isDraft && !isAnnulled && inv.openAmount > 0;
    const canCredit = !isDraft && !isAnnulled;
    return (
      <div className="flex gap-3 flex-wrap">
        {isDraft && (
          <button
            type="button"
            className="text-primary underline disabled:opacity-50"
            disabled={busyId === inv.id}
            onClick={() => handleEmit(inv)}
          >
            Emitir
          </button>
        )}
        {canPay && (
          <button
            type="button"
            className="text-primary underline"
            onClick={() => openPayment(inv)}
          >
            Receber
          </button>
        )}
        {canCredit && (
          <button
            type="button"
            className="text-primary underline"
            onClick={() => openCreditNote(inv)}
          >
            Nota de crédito
          </button>
        )}
        {(isDraft || inv.statusCode === "emitida") && (
          <button
            type="button"
            className="text-primary underline"
            onClick={() => openEdit(inv)}
          >
            Editar
          </button>
        )}
        {!isAnnulled && !isPaid && (
          <button
            type="button"
            className="text-destructive underline disabled:opacity-50"
            disabled={busyId === inv.id}
            onClick={() => handleAnnul(inv)}
          >
            Anular
          </button>
        )}
        {isDraft && (
          <button
            type="button"
            className="text-destructive underline disabled:opacity-50"
            disabled={busyId === inv.id}
            onClick={() => handleDelete(inv)}
          >
            Eliminar
          </button>
        )}
      </div>
    );
  };

  const clientSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      clientLookupOptions.map((option) => ({
        ...option,
        value: String(option.value),
      })),
    [clientLookupOptions],
  );

  const clientWorks = useMemo(
    () =>
      form.clientId
        ? works.filter((w) => String(w.clientId) === form.clientId)
        : works,
    [works, form.clientId],
  );

  const workSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      clientWorks.map((work) => ({
        value: String(work.id),
        label: work.name,
        description: work.code,
        keywords: work.code,
      })),
    [clientWorks],
  );

  return (
    <div className="space-y-4">
      <ListToolbar
        filter={filter}
        onFilterChange={setFilter}
        placeholder="Filtrar faturas…"
        summary={
          <span className="text-secondary-text text-sm">
            {shown.length} fatura(s) · Em aberto {formatCurrency(totalOpen)}
          </span>
        }
      >
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Nova fatura
        </button>
      </ListToolbar>

      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className={inputClass}
          aria-label="Filtrar por estado"
        >
          <option value="">Todos os estados</option>
          {statuses.map((status) => (
            <option key={status.id} value={status.code}>
              {status.name}
            </option>
          ))}
        </select>
        <select
          value={kindFilter}
          onChange={(event) => setKindFilter(event.target.value as "" | ClientInvoiceKind)}
          className={inputClass}
          aria-label="Filtrar por tipo"
        >
          <option value="">Faturas e notas de crédito</option>
          <option value="invoice">Apenas faturas</option>
          <option value="credit_note">Apenas notas de crédito</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-secondary-text">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(event) => setOpenOnly(event.target.checked)}
          />
          Apenas em aberto
        </label>
      </div>

      {error && !isCreateOpen && !paymentInvoice && !creditNoteInvoice && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: 1100 }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3" style={{ width: 150 }}>Nº</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Obra</th>
              <th className="px-4 py-3" style={{ width: 110 }}>Emissão</th>
              <th className="px-4 py-3" style={{ width: 110 }}>Vencimento</th>
              <th className="px-4 py-3 text-right" style={{ width: 120 }}>Total</th>
              <th className="px-4 py-3 text-right" style={{ width: 110 }}>Pago</th>
              <th className="px-4 py-3 text-right" style={{ width: 120 }}>Em aberto</th>
              <th className="px-4 py-3" style={{ width: 150 }}>Estado</th>
              <th className="px-4 py-3" style={{ width: 220 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-secondary-text">
                  A carregar…
                </td>
              </tr>
            ) : shown.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-secondary-text">
                  {filter !== "" ? "Sem resultados para o filtro." : "Sem faturas."}
                </td>
              </tr>
            ) : (
              shown.map((inv) => (
                <tr key={inv.id} className="border-b border-border last:border-b-0 align-top">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="flex items-center gap-2">
                      <span style={{ fontWeight: "var(--font-weight-medium)" }}>{inv.number}</span>
                      <KindBadge kind={inv.kind} />
                    </span>
                    {inv.originalInvoiceNumber && (
                      <span className="block text-xs text-secondary-text mt-0.5">
                        ref. {inv.originalInvoiceNumber}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 break-words">{inv.clientName}</td>
                  <td className="px-4 py-3 break-words">{inv.workName || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDateShort(inv.issueDate)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDateShort(inv.dueDate)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(inv.totalAmount)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(inv.paidAmount)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {inv.openAmount > 0 ? (
                      <span className="flex flex-col items-end gap-1">
                        <span style={{ fontWeight: "var(--font-weight-medium)" }}>
                          {formatCurrency(inv.openAmount)}
                        </span>
                        <AgingBadge bucket={inv.agingBucket} ageDays={inv.ageDays} />
                      </span>
                    ) : (
                      <span className="text-secondary-text">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge code={inv.statusCode} name={inv.statusName} />
                  </td>
                  <td className="px-4 py-3">{rowActions(inv)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create / edit invoice */}
      {isCreateOpen && (
        <Modal
          title={editInvoice ? `Editar fatura ${editInvoice.number}` : "Nova fatura"}
          onClose={closeForm}
          maxWidthClass="max-w-3xl"
          footer={
            <>
              <button
                type="button"
                onClick={closeForm}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isSaving}
                onClick={handleSaveInvoice}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {isSaving ? "A guardar…" : "Guardar"}
              </button>
            </>
          }
        >
          {error && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SearchableSelect<string>
              label="Cliente"
              required
              labelClassName="text-sm text-secondary-text"
              labelStyle={{}}
              panelPosition="fixed"
              value={form.clientId === "" ? null : form.clientId}
              onChange={(next) =>
                setForm({ ...form, clientId: next ?? "", workId: "" })
              }
              options={clientSelectOptions}
              loading={clientsLoading}
              placeholder="Selecione um cliente"
              searchPlaceholder="Procurar cliente…"
              disabled={editInvoice !== null}
            />
            <SearchableSelect<string>
              label="Obra (opcional)"
              labelClassName="text-sm text-secondary-text"
              labelStyle={{}}
              panelPosition="fixed"
              value={form.workId === "" ? null : form.workId}
              onChange={(next) => setForm({ ...form, workId: next ?? "" })}
              options={workSelectOptions}
              placeholder="—"
              searchPlaceholder="Procurar obra…"
            />
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Descrição
              <input
                type="text"
                value={form.description}
                maxLength={500}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                placeholder="Descritivo da fatura"
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Data de emissão
              <input
                type="date"
                value={form.issueDate}
                onChange={(event) => setForm({ ...form, issueDate: event.target.value })}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Data de vencimento
              <input
                type="date"
                value={form.dueDate}
                onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Valor base (€)
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.baseAmount}
                onChange={(event) => setForm({ ...form, baseAmount: event.target.value })}
                placeholder="0"
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              IVA (€)
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.vatAmount}
                onChange={(event) => setForm({ ...form, vatAmount: event.target.value })}
                placeholder="0"
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Total (€)
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.totalAmount}
                onChange={(event) => setForm({ ...form, totalAmount: event.target.value })}
                placeholder="Calculado a partir de base + IVA se vazio"
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Notas
              <textarea
                rows={2}
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
                className={inputClass}
              />
            </label>
          </div>
        </Modal>
      )}

      {/* Register payment */}
      {paymentInvoice && (
        <Modal
          title={`Registar recebimento — ${paymentInvoice.number}`}
          onClose={() => setPaymentInvoice(null)}
          maxWidthClass="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setPaymentInvoice(null)}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isSaving}
                onClick={handleRegisterPayment}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {isSaving ? "A registar…" : "Registar"}
              </button>
            </>
          }
        >
          {error && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {error}
            </div>
          )}
          <p className="text-sm text-secondary-text">
            Em aberto: {formatCurrency(paymentInvoice.openAmount)} de {formatCurrency(paymentInvoice.totalAmount)}
          </p>
          <div className="grid grid-cols-1 gap-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Valor recebido (€) *
              <input
                type="number"
                min="0"
                step="0.01"
                value={paymentAmount}
                onChange={(event) => setPaymentAmount(event.target.value)}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Data do recebimento
              <input
                type="date"
                value={paymentDate}
                onChange={(event) => setPaymentDate(event.target.value)}
                className={inputClass}
              />
            </label>
          </div>
        </Modal>
      )}

      {/* Credit note */}
      {creditNoteInvoice && (
        <Modal
          title={`Nota de crédito — ${creditNoteInvoice.number}`}
          onClose={() => setCreditNoteInvoice(null)}
          maxWidthClass="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setCreditNoteInvoice(null)}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isSaving}
                onClick={handleCreateCreditNote}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {isSaving ? "A criar…" : "Criar nota de crédito"}
              </button>
            </>
          }
        >
          {error && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {error}
            </div>
          )}
          <p className="text-sm text-secondary-text">
            Referencia a fatura {creditNoteInvoice.number} (total {formatCurrency(creditNoteInvoice.totalAmount)}). O valor não pode exceder o da fatura.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Valor total da NC (€) *
              <input
                type="number"
                min="0"
                step="0.01"
                value={creditAmount}
                onChange={(event) => setCreditAmount(event.target.value)}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Valor base (€)
              <input
                type="number"
                min="0"
                step="0.01"
                value={creditBase}
                onChange={(event) => setCreditBase(event.target.value)}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              IVA (€)
              <input
                type="number"
                min="0"
                step="0.01"
                value={creditVat}
                onChange={(event) => setCreditVat(event.target.value)}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Descrição
              <input
                type="text"
                value={creditDescription}
                maxLength={500}
                onChange={(event) => setCreditDescription(event.target.value)}
                placeholder="Motivo da nota de crédito"
                className={inputClass}
              />
            </label>
          </div>
        </Modal>
      )}

      {/* Bloqueio de crédito ao emitir — autorização com motivo [3.6.4] */}
      {blockModal && (
        <Modal
          title={`Emissão bloqueada — ${blockModal.invoice.number}`}
          onClose={() => setBlockModal(null)}
          maxWidthClass="max-w-lg"
          footer={
            <>
              <button
                type="button"
                onClick={() => setBlockModal(null)}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isOverriding}
                onClick={handleAuthorizeEmit}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {isOverriding ? "A autorizar…" : "Autorizar e emitir"}
              </button>
            </>
          }
        >
          <div
            className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {blockModal.message}
          </div>
          <p className="text-sm text-secondary-text">
            A emissão desta fatura foi bloqueada por incumprimento do cliente. Para emitir
            mesmo assim, indique o motivo da autorização (fica registado na fatura).
          </p>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Motivo da autorização *
            <textarea
              rows={3}
              value={overrideReason}
              onChange={(event) => setOverrideReason(event.target.value)}
              placeholder="Ex.: acordo de pagamento confirmado com o cliente"
              className={inputClass}
            />
          </label>
          {blockError && <div className="text-destructive text-sm">{blockError}</div>}
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cobrança — workflow + email [3.6.2]
// ---------------------------------------------------------------------------

function CobrancaTab({
  onOpenClient,
}: {
  onOpenClient: (clientId: number) => void;
}) {
  const [dashboard, setDashboard] = useState<CollectionDashboard | null>(null);
  const [filter, setFilter] = useState("");
  const [stageFilter, setStageFilter] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        setDashboard(
          await getCollectionDashboard(
            stageFilter ? { stage: stageFilter } : undefined,
          ),
        );
      } catch (err) {
        setError(errorMessage(err, "Não foi possível carregar o painel de cobrança."));
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [stageFilter],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Registar ação ------------------------------------------------------------
  const [actionRow, setActionRow] = useState<CollectionDashboardRow | null>(null);
  const [actionStage, setActionStage] = useState("due_notice");
  const [actionChannel, setActionChannel] = useState("phone");
  const [actionOutcome, setActionOutcome] = useState("");
  const [actionDate, setActionDate] = useState("");
  const [isSavingAction, setIsSavingAction] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const openAction = (row: CollectionDashboardRow) => {
    setActionRow(row);
    setActionStage(fallbackStage(row));
    setActionChannel("phone");
    setActionOutcome("");
    setActionDate(todayInput());
    setActionError(null);
  };

  const handleSaveAction = async () => {
    if (!actionRow || isSavingAction) return;
    setIsSavingAction(true);
    setActionError(null);
    try {
      await logCollectionActivity({
        clientId: actionRow.clientId,
        clientInvoiceId: actionRow.clientInvoiceId,
        stage: actionStage,
        channel: actionChannel,
        outcome: actionOutcome.trim() || null,
        activityDate: actionDate || null,
      });
      setActionRow(null);
      await load(true);
    } catch (err) {
      setActionError(errorMessage(err, "Não foi possível registar a ação."));
    } finally {
      setIsSavingAction(false);
    }
  };

  // Compor email -------------------------------------------------------------
  const [emailRow, setEmailRow] = useState<CollectionDashboardRow | null>(null);
  const [emailStage, setEmailStage] = useState("due_notice");
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const openEmail = async (row: CollectionDashboardRow) => {
    const stage = fallbackStage(row);
    setEmailRow(row);
    setEmailStage(stage);
    setEmailTo(row.clientEmail ?? "");
    setEmailSubject("");
    setEmailBody("");
    setEmailError(null);
    setIsLoadingEmail(true);
    try {
      const preview = await previewCollectionEmail(row.clientInvoiceId, stage);
      setEmailTo(preview.to ?? row.clientEmail ?? "");
      setEmailSubject(preview.subject);
      setEmailBody(preview.body);
    } catch (err) {
      setEmailError(errorMessage(err, "Não foi possível compor o email."));
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailRow || isSendingEmail) return;
    if (emailSubject.trim() === "" || emailBody.trim() === "") {
      setEmailError("Assunto e corpo do email são obrigatórios.");
      return;
    }
    setIsSendingEmail(true);
    setEmailError(null);
    try {
      await sendCollectionEmail({
        clientInvoiceId: emailRow.clientInvoiceId,
        stage: emailStage,
        to: emailTo.trim() || null,
        subject: emailSubject,
        body: emailBody,
      });
      setEmailRow(null);
      await load(true);
    } catch (err) {
      setEmailError(errorMessage(err, "Não foi possível enviar o email."));
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Histórico ----------------------------------------------------------------
  const [historyRow, setHistoryRow] = useState<CollectionDashboardRow | null>(null);
  const [historyActivities, setHistoryActivities] = useState<CollectionActivity[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const openHistory = async (row: CollectionDashboardRow) => {
    setHistoryRow(row);
    setHistoryActivities([]);
    setHistoryError(null);
    setIsLoadingHistory(true);
    try {
      setHistoryActivities(
        await getCollectionActivities({ clientInvoiceId: row.clientInvoiceId }),
      );
    } catch (err) {
      setHistoryError(errorMessage(err, "Não foi possível carregar o histórico."));
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const rows = dashboard?.rows ?? [];
  const normalizedFilter = filter.trim().toLowerCase();
  const shown =
    normalizedFilter === ""
      ? rows
      : rows.filter((row) =>
          [row.clientName, row.number, row.workName ?? ""]
            .join(" ")
            .toLowerCase()
            .includes(normalizedFilter),
        );

  const stageChips: { code: string; label: string; count?: number }[] = [
    { code: "", label: "Todas" },
    {
      code: "pre_due",
      label: STAGE_LABELS.pre_due,
      count: dashboard?.stageCounts.pre_due,
    },
    {
      code: "due_notice",
      label: STAGE_LABELS.due_notice,
      count: dashboard?.stageCounts.due_notice,
    },
    { code: "soft", label: STAGE_LABELS.soft, count: dashboard?.stageCounts.soft },
    { code: "firm", label: STAGE_LABELS.firm, count: dashboard?.stageCounts.firm },
  ];

  return (
    <div className="space-y-4">
      <ListToolbar
        filter={filter}
        onFilterChange={setFilter}
        placeholder="Filtrar por cliente, fatura ou obra…"
        summary={
          dashboard && (
            <span className="text-secondary-text text-sm">
              Em aberto {formatCurrency(dashboard.totalOpen)} · Vencido{" "}
              {formatCurrency(dashboard.totalOverdue)} · {dashboard.overdueInvoiceCount}{" "}
              vencida(s)
            </span>
          )
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        {stageChips.map((chip) => {
          const isActive = stageFilter === chip.code;
          return (
            <button
              key={chip.code || "all"}
              type="button"
              onClick={() => setStageFilter(chip.code)}
              className={`inline-flex items-center gap-1 px-3 py-1 text-sm cursor-pointer ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "border border-border text-foreground hover:bg-accent/50"
              }`}
              style={{ borderRadius: "var(--radius-button)" }}
            >
              {chip.label}
              {typeof chip.count === "number" && (
                <span className={isActive ? "opacity-80" : "text-secondary-text"}>
                  ({chip.count})
                </span>
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: 1150 }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3" style={{ width: 150 }}>Fatura nº</th>
              <th className="px-4 py-3" style={{ width: 110 }}>Vencimento</th>
              <th className="px-4 py-3 text-right" style={{ width: 80 }}>Dias</th>
              <th className="px-4 py-3 text-right" style={{ width: 120 }}>Saldo</th>
              <th className="px-4 py-3" style={{ width: 120 }}>Risco</th>
              <th className="px-4 py-3" style={{ width: 150 }}>Ação sugerida</th>
              <th className="px-4 py-3" style={{ width: 150 }}>Última ação</th>
              <th className="px-4 py-3" style={{ width: 240 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-secondary-text">
                  A carregar…
                </td>
              </tr>
            ) : shown.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-secondary-text">
                  {filter !== "" || stageFilter !== ""
                    ? "Sem faturas para o filtro."
                    : "Sem faturas em aberto para cobrança."}
                </td>
              </tr>
            ) : (
              shown.map((row) => (
                <tr
                  key={row.clientInvoiceId}
                  className="border-b border-border last:border-b-0 align-top"
                >
                  <td className="px-4 py-3 break-words">
                    <button
                      type="button"
                      className="text-primary underline text-left"
                      style={{ fontWeight: "var(--font-weight-medium)" }}
                      onClick={() => onOpenClient(row.clientId)}
                      title="Ver conta corrente do cliente"
                    >
                      {row.clientName}
                    </button>
                    {row.workName && (
                      <span className="block text-xs text-secondary-text mt-0.5">
                        {row.workName}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{row.number}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatDateShort(row.dueDate)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {row.ageDays != null && Number.isFinite(row.ageDays) ? (
                      <span className={row.ageDays > 0 ? "text-red-600" : ""}>
                        {row.ageDays > 0 ? `${row.ageDays}d` : "—"}
                      </span>
                    ) : (
                      <span className="text-secondary-text">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span style={{ fontWeight: "var(--font-weight-medium)" }}>
                      {formatCurrency(row.openAmount)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <RiskBadge level={row.riskLevel} />
                  </td>
                  <td className="px-4 py-3">
                    {row.suggestedStageName ?? (
                      <span className="text-secondary-text">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {row.lastActivityStage ? (
                      <span>
                        {STAGE_LABELS[row.lastActivityStage] ?? row.lastActivityStage}
                        {row.lastActivityDate && (
                          <span className="block text-xs text-secondary-text mt-0.5">
                            {formatDateShort(row.lastActivityDate)}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-secondary-text">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3 flex-wrap">
                      <button
                        type="button"
                        className="text-primary underline"
                        onClick={() => openAction(row)}
                      >
                        Registar ação
                      </button>
                      <button
                        type="button"
                        className="text-primary underline"
                        onClick={() => openEmail(row)}
                      >
                        Compor email
                      </button>
                      <button
                        type="button"
                        className="text-primary underline"
                        onClick={() => openHistory(row)}
                      >
                        Histórico
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Registar ação */}
      {actionRow && (
        <Modal
          title={`Registar ação — ${actionRow.number}`}
          onClose={() => setActionRow(null)}
          maxWidthClass="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setActionRow(null)}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isSavingAction}
                onClick={handleSaveAction}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {isSavingAction ? "A registar…" : "Registar"}
              </button>
            </>
          }
        >
          {actionError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {actionError}
            </div>
          )}
          <p className="text-sm text-secondary-text">
            {actionRow.clientName} · saldo {formatCurrency(actionRow.openAmount)}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Fase
              <select
                value={actionStage}
                onChange={(event) => setActionStage(event.target.value)}
                className={inputClass}
              >
                {Object.entries(STAGE_LABELS).map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Canal
              <select
                value={actionChannel}
                onChange={(event) => setActionChannel(event.target.value)}
                className={inputClass}
              >
                {CHANNEL_OPTIONS.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Data
              <input
                type="date"
                value={actionDate}
                onChange={(event) => setActionDate(event.target.value)}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Resultado / notas
              <textarea
                rows={3}
                value={actionOutcome}
                onChange={(event) => setActionOutcome(event.target.value)}
                placeholder="Ex.: cliente comprometeu-se a pagar até ao fim do mês"
                className={inputClass}
              />
            </label>
          </div>
        </Modal>
      )}

      {/* Compor email */}
      {emailRow && (
        <Modal
          title={`Email de cobrança — ${emailRow.number}`}
          onClose={() => setEmailRow(null)}
          maxWidthClass="max-w-2xl"
          footer={
            <>
              <button
                type="button"
                onClick={() => setEmailRow(null)}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isSendingEmail || isLoadingEmail}
                onClick={handleSendEmail}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {isSendingEmail ? "A enviar…" : "Enviar email"}
              </button>
            </>
          }
        >
          {emailError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {emailError}
            </div>
          )}
          <p className="text-sm text-secondary-text">
            Fase: {STAGE_LABELS[emailStage] ?? emailStage}
          </p>
          {isLoadingEmail ? (
            <p className="text-sm text-secondary-text py-4">A compor email…</p>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Para
                <input
                  type="email"
                  value={emailTo}
                  onChange={(event) => setEmailTo(event.target.value)}
                  placeholder="email@cliente.pt"
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Assunto *
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(event) => setEmailSubject(event.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Mensagem *
                <textarea
                  rows={10}
                  value={emailBody}
                  onChange={(event) => setEmailBody(event.target.value)}
                  className={inputClass}
                  style={{ fontFamily: "inherit" }}
                />
              </label>
            </div>
          )}
        </Modal>
      )}

      {/* Histórico */}
      {historyRow && (
        <Modal
          title={`Histórico de cobrança — ${historyRow.number}`}
          onClose={() => setHistoryRow(null)}
          maxWidthClass="max-w-2xl"
        >
          {historyError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {historyError}
            </div>
          )}
          {isLoadingHistory ? (
            <p className="text-sm text-secondary-text py-4">A carregar histórico…</p>
          ) : historyActivities.length === 0 ? (
            <p className="text-sm text-secondary-text py-4">
              Sem ações de cobrança registadas para esta fatura.
            </p>
          ) : (
            <ul className="space-y-3">
              {historyActivities.map((activity) => (
                <li
                  key={activity.id}
                  className="border border-border p-3"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span style={{ fontWeight: "var(--font-weight-medium)" }}>
                      {activity.stageName} · {activity.channelName}
                    </span>
                    <span className="text-xs text-secondary-text">
                      {formatDateShort(activity.activityDate)}
                    </span>
                  </div>
                  {activity.subject && (
                    <p className="text-sm mt-1">{activity.subject}</p>
                  )}
                  {activity.outcome && (
                    <p className="text-sm text-secondary-text mt-1 whitespace-pre-wrap">
                      {activity.outcome}
                    </p>
                  )}
                  {activity.emailTo && (
                    <p className="text-xs text-secondary-text mt-1">
                      Para: {activity.emailTo}
                      {activity.emailStatus ? ` · ${activity.emailStatus}` : ""}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </div>
  );
}
